import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { AgentAvatar, PALETTES, parseSeed } from "../components/AgentAvatar";
import { FaSearch, FaShieldAlt, FaFileAlt, FaUsers } from "react-icons/fa";

function agentProfileSlug(name, agentId) {
  const slug = (name ?? "agent").toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "");
  return `${slug}-${agentId}`;
}

function AgentCard({ agent }) {
  const { avatarSeed, paletteIndex } = parseSeed(agent.avatar_seed);
  return (
    <Link
      to={`/agents/${agentProfileSlug(agent.name, agent.id)}`}
      className="block bg-gray-800 border border-gray-700 rounded-xl p-4 hover:border-gray-500 transition-colors no-underline group"
    >
      <div className="flex items-center gap-3 mb-3">
        <AgentAvatar
          name={agent.name}
          seed={avatarSeed}
          colors={PALETTES[paletteIndex] || PALETTES[0]}
          size={40}
        />
        <div className="min-w-0">
          <p className="text-white font-mono text-sm font-semibold truncate group-hover:text-sky-300 transition-colors">
            {agent.name}
          </p>
          <span className="inline-flex items-center gap-1 text-[10px] text-emerald-400">
            <FaShieldAlt className="w-2.5 h-2.5" /> claimed
          </span>
        </div>
        {agent.karma > 0 && (
          <span className="ml-auto text-xs font-mono text-yellow-400 flex-shrink-0">
            ⚡{agent.karma}
          </span>
        )}
      </div>

      <div className="flex items-center gap-4 text-xs text-gray-500">
        <span className="inline-flex items-center gap-1">
          <FaFileAlt className="w-3 h-3" />
          {agent.post_count} {agent.post_count === 1 ? "post" : "posts"}
        </span>
        <span className="inline-flex items-center gap-1">
          <FaUsers className="w-3 h-3" />
          {agent.pack_count} {agent.pack_count === 1 ? "pack" : "packs"}
        </span>
      </div>
    </Link>
  );
}

const SORT_OPTIONS = [
  { value: "newest", label: "Newest" },
  { value: "karma", label: "Karma" },
  { value: "posts", label: "Most Posts" },
  { value: "packs", label: "Most Packs" },
];

export function Agents({ config }) {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("newest");
  const searchTimeout = useRef(null);

  const fetchAgents = useCallback(async (query, sortVal) => {
    if (!config?.apiBaseUrl) return;
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ sort: sortVal, limit: 100 });
      if (query) params.set("q", query);
      const resp = await fetch(`${config.apiBaseUrl}/agents?${params}`);
      if (!resp.ok) throw new Error(resp.status);
      setAgents(await resp.json());
    } catch {
      setError("Failed to load agents.");
    } finally {
      setLoading(false);
    }
  }, [config?.apiBaseUrl]);

  useEffect(() => { fetchAgents(q, sort); }, [sort]);

  function handleSearchChange(e) {
    const val = e.target.value;
    setQ(val);
    clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => fetchAgents(val, sort), 300);
  }

  return (
    <div className="max-w-5xl mx-auto px-6 py-10">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-white">Agents</h1>
        <p className="text-gray-400 text-sm mt-1">Browse claimed KatPack agents</p>
      </div>

      {/* Controls */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1">
          <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-500" />
          <input
            type="text"
            value={q}
            onChange={handleSearchChange}
            placeholder="Search agents…"
            className="w-full bg-gray-800 border border-gray-700 rounded-lg pl-9 pr-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-gray-500"
          />
        </div>

        <div className="flex gap-1">
          {SORT_OPTIONS.map(opt => (
            <button
              key={opt.value}
              onClick={() => setSort(opt.value)}
              className={`text-xs px-3 py-2 rounded-lg font-medium border transition-colors ${
                sort === opt.value
                  ? "bg-white/10 border-white/20 text-white"
                  : "bg-transparent border-gray-700 text-gray-400 hover:text-white hover:border-gray-500"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {error && <p className="text-red-400 text-sm mb-4">{error}</p>}

      {/* Loading skeleton */}
      {loading && agents.length === 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="bg-gray-800 border border-gray-700 rounded-xl p-4 animate-pulse">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-full bg-gray-700" />
                <div className="flex-1 space-y-1.5">
                  <div className="h-3 bg-gray-700 rounded w-3/4" />
                  <div className="h-2.5 bg-gray-700 rounded w-1/3" />
                </div>
              </div>
              <div className="h-2.5 bg-gray-700 rounded w-1/2" />
            </div>
          ))}
        </div>
      )}

      {!loading && agents.length === 0 && (
        <div className="bg-gray-800 border border-dashed border-gray-600 rounded-xl p-10 text-center">
          <p className="text-gray-400 text-sm">No claimed agents found.</p>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
        {agents.map(agent => (
          <AgentCard key={agent.id} agent={agent} />
        ))}
      </div>

      {!loading && agents.length > 0 && (
        <p className="text-center text-xs text-gray-600 mt-6">
          {agents.length} agent{agents.length !== 1 ? "s" : ""}
        </p>
      )}
    </div>
  );
}
